import React, { FC } from 'react';
import Typography from 'src/common/components/Typography';
import Icon from 'src/common/components/IconDS22';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import type { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import getCaptainedCopy from 'src/common/getCaptainedCopy';
import { getBookNowDepartureCopy, getTripHour } from 'src/common/helpers';
import newTZDate, { formatDate } from 'src/common/utils/dateHelpers';
import { BOOK_NOW_DATE_FORMAT } from 'src/common/constants';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { completePriceFormatter } from 'src/common/utils/currency';
import { useTranslation } from 'next-i18next';
import styles from './InstabookOption.module.scss';

export type Props = {
  instabook: InstabookAvailability;
  onClick: () => void;
  selectedInstabookId?: InstabookAvailability['id'];
};

const InstabookOption: FC<Props> = ({
  instabook,
  selectedInstabookId,
  onClick,
}) => {
  const [tDirect] = useTranslation('book-now-direct');
  const [tCommon] = useTranslation('common');
  const [tListing] = useTranslation('listing-page');
  const highlighted = selectedInstabookId === instabook.id;
  const captainedCopy = getCaptainedCopy(instabook.captained, instabook.captain_cost_included);
  const duration = getTripHour(instabook.trip_length);

  const { currencies } = useCurrencyContext();
  const bookingCurrency = currencies?.display_currencies?.find(
    (c) => c.code === instabook.transaction.amounts.renter_currency.code,
  );
  const departTime = getBookNowDepartureCopy(instabook.instabook_trip_times, tListing);
  const dateFnsLocale = useDateFnsLocale();
  const departDate = formatDate(
    newTZDate(instabook.date),
    BOOK_NOW_DATE_FORMAT,
    dateFnsLocale,
  );
  const price = bookingCurrency ? completePriceFormatter(
    instabook.transaction.amounts.renter_booking_amount,
    bookingCurrency.code,
    bookingCurrency.symbol,
    bookingCurrency.precision,
  ) : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={getClassNames(styles, 'root', classNames({ highlighted }))}
    >
      <div className={styles.header}>
        <Typography
          type="span"
          size="body-l"
        >
          {tDirect('duration', { count: duration })}
        </Typography>
        <div className={styles.price}>
          <Typography
            type="span"
            size="body-l"
            weight="semibold"
          >
            {price}
          </Typography>
        </div>
        <div className={styles.date}>
          <Typography
            type="p"
            size="body-s"
            weight="semibold"
          >
            {departDate}
          </Typography>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.duration}>
          <Icon id="clock" />
          <Typography
            type="span"
            size="body-s"
          >
            {departTime}
          </Typography>
        </div>

        <div className={styles.captainOption}>
          <Icon id="captain" />
          <div className={styles.captainOptionText}>
            <Typography
              type="span"
              size="body-s"
              weight="semibold"
              data-test="instabook-captain-options-booknow"
            >
              {tCommon(captainedCopy.heading)}
            </Typography>
            <Typography
              type="span"
              size="body-s"
              data-test="instabook-captain-description-booknow"
            >
              {tCommon(captainedCopy.body)}
            </Typography>
          </div>
        </div>
      </div>
    </button>
  );
};

export default InstabookOption;
